import React, { useState, useEffect, useRef } from 'react';
import { Search, ShoppingBag, User, Phone, ChevronDown, Menu, X } from 'lucide-react';

const API_BASE_URL = 'https://rudrakshenterprises.liveindiatech.com/rudraksh_backend';

// Robust helper function to fix all variations of image URLs for live server
const getCorrectImageUrl = (imgInput) => {
    // Agar image object ya nested structure mein hai
    let url = imgInput;
    if (typeof imgInput === 'object' && imgInput !== null) {
        url = imgInput.image || imgInput.url || '';
    }

    if (!url || typeof url !== 'string') return 'https://images.unsplash.com/photo-1540420773420-3366772f4999?auto=format&fit=crop&w=600&q=80';

    // Agar URL mein local IP hai, toh use live domain se replace karein
    if (url.includes('127.0.0.1:8000') || url.includes('localhost:8000')) {
        return url.replace(/https?:\/\/(127\.0\.0\.1|localhost):8000/, 'https://rudrakshenterprises.liveindiatech.com/rudraksh_backend');
    }

    // Agar URL http ya https se shuru nahi hota, toh use media path ya domain ke sath jodein
    if (url.startsWith('http://') || url.startsWith('https://')) {
        return url;
    }

    if (url.startsWith('/')) {
        if (!url.startsWith('/rudraksh_backend') && !url.startsWith('/media')) {
            return `https://rudrakshenterprises.liveindiatech.com/rudraksh_backend${url}`;
        }
        return `https://rudrakshenterprises.liveindiatech.com${url}`;
    }

    return `https://rudrakshenterprises.liveindiatech.com/rudraksh_backend/media/${url}`;
};

export default function Navbar({
    cartCount,
    setIsCartOpen,
    setActiveCategory,
    setCurrentPage,
    setSearchQuery,
    onViewDetails,
    pujaProducts = [],
    herbalProducts = [],
    jaivikProducts = [],
    ayurvedicProducts = [],
    organicProducts = [],
    adminProducts = []
}) {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isMobileCategoryOpen, setIsMobileCategoryOpen] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [storedCartCount, setStoredCartCount] = useState(cartCount || 0);
    const searchRef = useRef(null);

    // Live server refresh par cart count localStorage se sync rakhne ke liye
    useEffect(() => {
        const updateCartCountFromStorage = () => {
            const savedCart = localStorage.getItem('rudraksh_cart');
            if (savedCart) {
                try {
                    const parsedCart = JSON.parse(savedCart);
                    const total = parsedCart.reduce((acc, item) => acc + item.quantity, 0);
                    setStoredCartCount(total);
                } catch (e) {
                    console.error("Cart count read error:", e);
                }
            } else {
                setStoredCartCount(cartCount || 0);
            }
        };

        updateCartCountFromStorage();

        window.addEventListener('storage', updateCartCountFromStorage);
        const interval = setInterval(updateCartCountFromStorage, 500);

        return () => {
            window.removeEventListener('storage', updateCartCountFromStorage);
            clearInterval(interval);
        };
    }, [cartCount]);

    const categories = [
        { id: 'all', name: 'All Products' },
        { id: 'puja', name: '1. Puja Samagri' },
        { id: 'herbal', name: '2. Herbal Products' },
        { id: 'jaivik', name: '3. Jaivik Products' },
        { id: 'ayurvedic', name: '4. Ayurvedic Products' },
        { id: 'organic', name: '5. Organic Products' },
    ];

    const allProductsList = [
        ...adminProducts,
        ...pujaProducts,
        ...herbalProducts,
        ...jaivikProducts,
        ...ayurvedicProducts,
        ...organicProducts
    ];

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleCategoryClick = (catId, e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        setIsDropdownOpen(false);
        setIsMobileMenuOpen(false);
        setIsMobileCategoryOpen(false);

        if (typeof setActiveCategory === 'function') {
            setActiveCategory(catId);
        }
        if (typeof setCurrentPage === 'function') {
            setCurrentPage('category');
        }
        if (typeof setSearchQuery === 'function') {
            setSearchQuery('');
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const changePage = (pageName, e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        setIsDropdownOpen(false);
        setIsMobileMenuOpen(false);
        setIsMobileCategoryOpen(false);

        if (typeof setActiveCategory === 'function') {
            setActiveCategory('all');
        }
        if (typeof setSearchQuery === 'function') {
            setSearchQuery('');
        }
        if (typeof setCurrentPage === 'function') {
            setCurrentPage(pageName);
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleSearchInput = (e) => {
        const val = e.target.value;
        setSearchInput(val);
        if (setSearchQuery) setSearchQuery(val);

        if (val.trim() !== '') {
            const filtered = allProductsList.filter(p =>
                p.name && p.name.toLowerCase().includes(val.toLowerCase())
            );
            const uniqueFiltered = Array.from(new Set(filtered.map(p => p.id)))
                .map(id => filtered.find(p => p.id === id));

            setSuggestions(uniqueFiltered.slice(0, 5));
            setShowSuggestions(true);
        } else {
            setSuggestions([]);
            setShowSuggestions(false);
        }
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        if (setSearchQuery) setSearchQuery(searchInput);
        if (setActiveCategory) setActiveCategory('all');
        changePage('category', e);
        setShowSuggestions(false);
    };

    return (
        <header className="sticky top-0 z-50 bg-white shadow-md">
            {/* Top Bar */}
            <div className="bg-orange-600 text-white text-xs py-2 px-4 shadow-sm">
                <div className="max-w-7xl mx-auto flex justify-between items-center">
                    <div className="flex gap-4 font-bold"></div>
                    <div className="flex items-center gap-4">
                        <span className="hidden sm:inline font-bold text-orange-100">Welcome to Rudraksh Enterprises</span>

                        <div className="relative w-full md:w-auto" ref={searchRef}>
                            <form onSubmit={handleSearchSubmit} className="flex items-center bg-white border border-orange-400 rounded-lg overflow-hidden px-2 py-0.5 shadow-sm w-full">
                                <input
                                    type="text"
                                    placeholder="Search products..."
                                    value={searchInput}
                                    onChange={handleSearchInput}
                                    onFocus={() => {
                                        if (searchInput.trim() !== '') setShowSuggestions(true);
                                    }}
                                    className="text-xs outline-none px-1 w-full md:w-52 text-gray-800 bg-transparent font-medium"
                                />
                                <button type="submit" className="text-orange-600 hover:text-orange-700 cursor-pointer border-none bg-transparent">
                                    <Search size={14} />
                                </button>
                            </form>

                            {/* Search Suggestions Dropdown */}
                            {showSuggestions && suggestions.length > 0 && (
                                <div className="absolute left-0 right-0 mt-2 bg-white rounded-xl shadow-lg border border-gray-100 max-h-64 overflow-y-auto z-50">
                                    {suggestions.map((product, idx) => {
                                        const suggestionImage = getCorrectImageUrl(product.image);
                                        return (
                                            <div
                                                key={product.id || idx}
                                                onClick={() => {
                                                    if (typeof onViewDetails === 'function') {
                                                        onViewDetails(product);
                                                    }
                                                    setShowSuggestions(false);
                                                    setSearchInput('');
                                                }}
                                                className="flex items-center gap-3 p-2.5 hover:bg-orange-50 transition cursor-pointer border-b border-gray-50 last:border-none"
                                            >
                                                <div className="w-10 h-10 bg-white rounded-lg border border-gray-200 overflow-hidden flex items-center justify-center p-0.5 shrink-0">
                                                    <img
                                                        src={suggestionImage}
                                                        alt={product.name}
                                                        onError={(e) => { e.target.onerror = null; e.target.src = 'https://images.unsplash.com/photo-1540420773420-3366772f4999?auto=format&fit=crop&w=600&q=80'; }}
                                                        className="w-full h-full object-contain rounded"
                                                    />
                                                </div>
                                                <div className="text-left overflow-hidden">
                                                    <h4 className="text-xs font-bold text-gray-900 truncate">{product.name}</h4>
                                                    <span className="text-[11px] font-black text-orange-600">₹{product.price}</span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                        <button
                            onClick={(e) => changePage('auth', e)}
                            className="flex items-center gap-1 bg-white text-orange-600 px-3 py-1 rounded text-xs font-black hover:bg-orange-50 transition shadow-sm cursor-pointer border-none"
                        >
                            <User size={12} /> Register / Login
                        </button>
                    </div>
                </div>
            </div>

            {/* Main Navbar */}
            <div className="bg-white border-b border-orange-100 text-gray-900">
                <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-4 cursor-pointer" onClick={(e) => changePage('home', e)}>
                        <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center">
                            <img
                                src="/logo.png"
                                alt="Logo"
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.parentNode.innerHTML = '<span class="bg-orange-600 text-white font-black text-2xl px-4 py-3 rounded-xl tracking-wider">RE</span>';
                                }}
                                className="w-full h-full object-contain"
                            />
                        </div>

                        <div>
                            <h1 className="text-xl md:text-2xl font-black tracking-wide text-gray-900 leading-tight">
                                Rudraksh <span className="text-orange-600">Enterprises</span>
                            </h1>
                            <p className="text-[10px] md:text-xs text-orange-600 font-extrabold uppercase tracking-widest mt-0.5">Pure & Natural Wellness</p>
                        </div>
                    </div>

                    <nav className="hidden md:flex items-center gap-8 text-sm font-extrabold">
                        <button
                            onClick={(e) => changePage('home', e)}
                            className="hover:text-orange-600 transition bg-transparent border-none text-gray-800 cursor-pointer text-sm font-extrabold"
                        >
                            Home
                        </button>

                        <div
                            className="relative"
                            onMouseEnter={() => setIsDropdownOpen(true)}
                            onMouseLeave={() => setIsDropdownOpen(false)}
                        >
                            <button
                                onClick={(e) => {
                                    if (e) {
                                        e.preventDefault();
                                        e.stopPropagation();
                                    }
                                    setIsDropdownOpen(prev => !prev);
                                }}
                                className="flex items-center gap-1 hover:text-orange-600 transition py-2 bg-transparent border-none text-gray-800 cursor-pointer text-sm font-extrabold"
                            >
                                Our Category <ChevronDown size={14} className={`transform transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                            </button>
                            {isDropdownOpen && (
                                <div className="absolute top-full left-0 w-56 bg-white text-gray-800 shadow-xl rounded-2xl py-2 mt-1 border border-orange-200 z-50">
                                    {categories.map(cat => (
                                        <button
                                            key={cat.id}
                                            onClick={(e) => handleCategoryClick(cat.id, e)}
                                            className="w-full text-left px-4 py-2.5 text-xs hover:bg-orange-50 hover:text-orange-600 font-extrabold transition bg-transparent border-none cursor-pointer"
                                        >
                                            {cat.name}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        <button
                            onClick={(e) => changePage('contact', e)}
                            className="hover:text-orange-600 transition bg-transparent border-none text-gray-800 cursor-pointer text-sm font-extrabold"
                        >
                            Contact Us
                        </button>
                    </nav>

                    <div className="flex items-center gap-3 md:gap-4">
                        <div className="hidden lg:block text-right">
                            <p className="text-[10px] text-gray-400 uppercase tracking-wider font-bold">Call for Order</p>
                            <p className="text-sm font-black text-orange-600 flex items-center gap-1">
                                <Phone size={14} /> +91 9911992162
                            </p>
                        </div>

                        <button
                            onClick={() => { if (setIsCartOpen) setIsCartOpen(true); }}
                            className="relative bg-orange-600 hover:bg-orange-700 text-white p-2.5 rounded-xl transition shadow-md flex items-center justify-center border-none cursor-pointer"
                        >
                            <ShoppingBag size={20} />
                            {storedCartCount > 0 && (
                                <span className="absolute -top-1.5 -right-1.5 bg-red-600 text-white text-[10px] w-5 h-5 rounded-full flex items-center justify-center font-black border-2 border-white shadow">
                                    {storedCartCount}
                                </span>
                            )}
                        </button>

                        <button
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            className="md:hidden text-orange-600 p-2 rounded-xl bg-orange-50 hover:bg-orange-100 transition border-none cursor-pointer"
                        >
                            {isMobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
                        </button>
                    </div>
                </div>

                {isMobileMenuOpen && (
                    <div className="md:hidden bg-white border-t border-orange-100 px-4 py-5 space-y-4 shadow-xl">
                        <button
                            onClick={(e) => changePage('home', e)}
                            className="block w-full text-left text-sm font-bold hover:text-orange-600 transition py-2 border-b border-gray-100 bg-transparent text-gray-800 border-none"
                        >
                            Home
                        </button>

                        <div>
                            <button
                                onClick={() => setIsMobileCategoryOpen(!isMobileCategoryOpen)}
                                className="w-full flex items-center justify-between text-sm font-bold hover:text-orange-600 transition py-2 border-b border-gray-100 bg-transparent text-gray-800 border-none"
                            >
                                <span>Our Categories</span>
                                <ChevronDown size={16} className={`transform transition-transform ${isMobileCategoryOpen ? 'rotate-180' : ''}`} />
                            </button>

                            {isMobileCategoryOpen && (
                                <div className="pl-4 py-2 space-y-2 bg-orange-50/50 rounded-2xl mt-2 border border-orange-100">
                                    {categories.map(cat => (
                                        <button
                                            key={cat.id}
                                            onClick={(e) => handleCategoryClick(cat.id, e)}
                                            className="block w-full text-left text-xs text-gray-700 hover:text-orange-600 py-1.5 font-bold transition bg-transparent border-none cursor-pointer"
                                        >
                                            • {cat.name}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        <button
                            onClick={(e) => changePage('contact', e)}
                            className="block w-full text-left text-sm font-bold hover:text-orange-600 transition py-2 border-b border-gray-100 bg-transparent text-gray-800 border-none"
                        >
                            Contact Us
                        </button>

                        <button
                            onClick={(e) => changePage('auth', e)}
                            className="block w-full text-left text-sm font-bold text-orange-600 transition py-2 bg-transparent border-none cursor-pointer"
                        >
                            Register / Login
                        </button>
                    </div>
                )}
            </div>
        </header>
    );
}